import React, { useState, useEffect } from "react";

import { makeStyles } from "@mui/styles";
import {
  Grid,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Elements,
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import { cardBodyStyle, ButtonStyle0 } from "components/cardBodyStyle";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const StripeCheckoutForm = ({
  clientSecret,
  amount,
  paymentIntentId,
  onSuccess,
  onClose,
  onError,
  paymentData, // Datos adicionales del pago
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      setError("Stripe no está inicializado correctamente");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message);
        setLoading(false);
        return;
      }

      const { error: confirmError, paymentIntent } =
        await stripe.confirmPayment({
          elements,
          clientSecret,
          confirmParams: {
            return_url: `${window.location.origin}/payment-success?payment_intent=${paymentIntentId}`,
          },
          redirect: "if_required",
        });

      if (confirmError) {
        setError(confirmError.message);
      } else {
        // El pago fue exitoso, obtener detalles completos
        const confirmedPayment =
          await stripe.retrievePaymentIntent(clientSecret);

        if (confirmedPayment.paymentIntent) {
          const paymentDetails = {
            paymentIntentId: confirmedPayment.paymentIntent.id,
            status: confirmedPayment.paymentIntent.status,
            amount: confirmedPayment.paymentIntent.amount / 100, // Convertir de centavos
            currency: confirmedPayment.paymentIntent.currency,
            paymentMethod: confirmedPayment.paymentIntent.payment_method,
            receiptEmail: confirmedPayment.paymentIntent.receipt_email,
            created: new Date(confirmedPayment.paymentIntent.created * 1000), // Convertir timestamp
            ...paymentData, // Incluir datos adicionales
          };

          // Llamar al callback de éxito con todos los detalles
          onSuccess(paymentDetails);
        }
      }
    } catch (err) {
      console.error("Error en el pago:", err);
      setError("Ocurrió un error inesperado durante el pago");
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <PaymentElement
        options={{
          layout: {
            type: "tabs",
            defaultCollapsed: false,
            //  Solo muestra tarjeta (oculta Cash App, Amazon Pay, etc.)
            paymentMethodOrder: ["card"],
          },
        }}
      />

      <Grid container sx={{ mt: 3 }}>
        <Grid item xs={12} md={6} sx={{ mt: 1, p: 1 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={loading}
            fullWidth
          >
            Cancelar
          </Button>
        </Grid>
        <Grid item xs={12} md={6} sx={{ mt: 1, p: 1 }}>
          <ButtonStyle0
            type="submit"
            disabled={!stripe || loading}
            style={{ width: "100%" }}
          >
            {loading ? <CircularProgress size={24} /> : `Pagar $${amount}`}
          </ButtonStyle0>
        </Grid>
      </Grid>
    </form>
  );
};

const useStyles = makeStyles(cardBodyStyle);

const StripePaymentFormAPI = ({
  open,
  onClose,
  clientSecret,
  amount,
  paymentIntentId,
  onSuccess,
  onError,
  paymentData, // Datos adicionales para enviar al backend
}) => {
  const classes = useStyles();

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (open && clientSecret) {
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [open, clientSecret]);

  const options = {
    clientSecret,
    appearance: {
      theme: "stripe",
      variables: {
        colorPrimary: "#1976d2",
      },
    },
  };

  return (
    <Grid container sx={{ p: 3 }}>
      <Grid item xs={12}>
        {!clientSecret ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              p: 3,
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Creando sesión de pago...</Typography>
          </Box>
        ) : isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              p: 3,
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Inicializando Stripe...</Typography>
          </Box>
        ) : (
          <>
            <div
              className={classes.titleCardFilter}
              style={{ fontSize: 24, textAlign: "left" }}
            >
              Tarjeta de crédito o débito
            </div>
            <Elements stripe={stripePromise} options={options}>
              <StripeCheckoutForm
                clientSecret={clientSecret}
                amount={amount}
                paymentIntentId={paymentIntentId}
                onSuccess={onSuccess}
                onClose={onClose}
                onError={onError}
                paymentData={paymentData}
              />
            </Elements>
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default StripePaymentFormAPI;
